import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { ArrowDownTrayIcon, FolderIcon } from '@heroicons/react/24/outline'
import {
  SkillApi,
  CourseSkillLink,
  SkillTreeNode,
  skillFolderBasename,
} from '../../services/skillApi'
import SkillTree from './SkillTree'

interface CourseSkillLinksPanelProps {
  courseId: number
  /** Selected file id for highlight in tree */
  selectedFileId?: number | null
  /** When learner clicks a file — parent shows content in left panel */
  onSelectFile?: (node: SkillTreeNode) => void
}

const CourseSkillLinksPanel = ({
  courseId,
  selectedFileId,
  onSelectFile,
}: CourseSkillLinksPanelProps) => {
  const [links, setLinks] = useState<CourseSkillLink[]>([])
  const [loading, setLoading] = useState(true)
  const [activeLink, setActiveLink] = useState<CourseSkillLink | null>(null)
  const [tree, setTree] = useState<SkillTreeNode[]>([])
  const [downloading, setDownloading] = useState(false)

  const loadTreeForLink = async (link: CourseSkillLink) => {
    const data = await SkillApi.getLinkedTree(link.nodeId, courseId)
    setTree(data.tree || [])
    setActiveLink(link)
  }

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const data = await SkillApi.listCourseSkillLinks(courseId, false)
        setLinks(data)
        // Auto-open when only one folder is attached
        if (data.length === 1) {
          await loadTreeForLink(data[0])
        }
      } catch (e: any) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [courseId])

  const openLink = async (link: CourseSkillLink) => {
    try {
      await loadTreeForLink(link)
    } catch (e: any) {
      toast.error(e?.message || 'Không mở được skill')
    }
  }

  const handleDownloadFolder = async (node: SkillTreeNode) => {
    try {
      setDownloading(true)
      const folderName = skillFolderBasename(node.relativePath, node.name)
      await SkillApi.downloadFolder(node.id, folderName, { courseId })
      toast.success(`Đã tải ${folderName}`)
    } catch (e: any) {
      toast.error(e?.message || 'Tải thất bại')
    } finally {
      setDownloading(false)
    }
  }

  const handleSelectFile = (node: SkillTreeNode) => {
    onSelectFile?.(node)
  }

  if (loading) {
    return <p className="text-sm text-m2-on-surface-variant">Đang tải skill đính kèm...</p>
  }

  if (links.length === 0) {
    return null
  }

  if (activeLink) {
    return (
      <div className="space-y-3">
        {links.length > 1 && (
          <button
            type="button"
            className="text-sm text-m2-primary hover:underline"
            onClick={() => {
              setActiveLink(null)
              setTree([])
            }}
          >
            ← Quay lại danh sách skill
          </button>
        )}
        <div className="flex items-center justify-between gap-2">
          <h4 className="font-bold text-sm text-m2-on-surface">
            {activeLink.title || activeLink.nodeName}
          </h4>
          <button
            type="button"
            disabled={downloading}
            title="Tải thư mục (ZIP)"
            onClick={() =>
              handleDownloadFolder({
                id: activeLink.nodeId,
                packId: activeLink.packId,
                parentId: null,
                nodeType: 'FOLDER',
                name: skillFolderBasename(
                  activeLink.relativePath,
                  activeLink.nodeName || activeLink.title || 'skill'
                ),
                relativePath: activeLink.relativePath || '',
                children: [],
              })
            }
            className="inline-flex items-center gap-1 rounded-md border border-m2-outline px-2 py-1 text-[11px] hover:bg-m2-surface-high disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-3.5 w-3.5" />
            ZIP
          </button>
        </div>
        <p className="text-[11px] text-m2-on-surface-variant">
          Duyệt cây thư mục — click file để xem nội dung bên khung trái.
        </p>
        <div className="max-h-[min(60vh,520px)] overflow-y-auto rounded border border-m2-outline-variant bg-m2-surface-low">
          <SkillTree
            nodes={tree}
            selectedId={selectedFileId}
            onSelectFile={handleSelectFile}
            onDownloadFolder={handleDownloadFolder}
          />
        </div>
      </div>
    )
  }

  return (
    <div>
      <h4 className="mb-3 border-b border-m2-outline-variant pb-1 text-sm font-bold uppercase tracking-wider text-m2-on-surface">
        Bộ skill
      </h4>
      <div className="space-y-2">
        {links.map((link) => (
          <button
            key={link.id}
            type="button"
            onClick={() => openLink(link)}
            className="flex w-full items-start space-x-3 rounded-lg border border-m2-outline-variant bg-m2-surface-container p-3 text-left transition-colors hover:border-m2-primary/40 hover:bg-m2-surface-low"
          >
            <FolderIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1">
              <h5 className="line-clamp-2 text-sm font-medium text-m2-on-surface">
                {link.title || link.nodeName}
              </h5>
              <span className="mt-1 block truncate text-[10px] text-m2-on-surface-variant">
                {link.packName}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

export default CourseSkillLinksPanel
